import { useEffect, useRef } from 'react'
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { toast } from 'sonner'

import { useAuthTokenSetter, useAuthTokenValue } from '@/entities/auth'
import { ERROR_CODE } from '@/shared/model/constants'

import { AXIOS_INSTANCE } from './axios-client'
import { usePostUsersAuthRefresh } from './generated'

export function useRefreshTokenInterceptor() {
    const { token } = useAuthTokenValue()
    const { tokenSetter } = useAuthTokenSetter()
    const isRefreshingRef = useRef(false)
    const requestsQueueRef = useRef<
        Array<{
            resolve: (value: unknown) => void
            reject: (reason?: unknown) => void
        }>
    >([])
    const { mutateAsync: mutateRefreshToken } = usePostUsersAuthRefresh({
        mutation: {
            retry: 0,
        },
    })

    const processQueue = (error: unknown, access_token: string | null) => {
        for (const { resolve, reject } of requestsQueueRef.current) {
            if (error) {
                reject(error)
            } else {
                resolve(access_token)
            }
        }
        requestsQueueRef.current = []
    }

    useEffect(() => {
        // Response interceptor
        const responseInterceptor = AXIOS_INSTANCE.interceptors.response.use(
            (response: AxiosResponse) => {
                return response
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

                if (error.response?.status === 401 && !originalRequest._retry) {
                    if (isRefreshingRef.current) {
                        return new Promise((resolve, reject) => {
                            requestsQueueRef.current.push({ resolve, reject })
                        })
                            .then((access_token) => {
                                return AXIOS_INSTANCE({
                                    ...originalRequest,
                                    headers: {
                                        ...originalRequest.headers,
                                        Authorization: `Bearer ${access_token}`,
                                    },
                                })
                            })
                            .catch((error_) => {
                                return Promise.reject(error_)
                            })
                    }

                    originalRequest._retry = true
                    isRefreshingRef.current = true

                    return mutateRefreshToken(undefined)
                        .then((response) => {
                            tokenSetter(response.access_token)

                            AXIOS_INSTANCE.defaults.headers.Authorization = `Bearer ${response.access_token}`

                            processQueue(null, response.access_token)

                            const retryConfig = {
                                ...originalRequest,
                                headers: {
                                    ...originalRequest.headers,
                                    Authorization: `Bearer ${response.access_token}`,
                                },
                            }

                            return AXIOS_INSTANCE(retryConfig)
                        })
                        .catch((error) => {
                            if (
                                error?.response?.status === 401 ||
                                error?.error_code ===
                                    ERROR_CODE.USER_REFRESH_TOKEN_COOKIE_NOT_FOUND ||
                                error?.error_code === ERROR_CODE.USER_REFRESH_TOKEN_EXPIRED
                            ) {
                                processQueue(error, null)
                                tokenSetter(undefined)
                                AXIOS_INSTANCE.defaults.headers.Authorization = ''

                                toast.error('Ваша сессия истекла, пожалуйста, авторизуйтесь заново')
                            }

                            return Promise.reject(error)
                        })
                        .finally(() => {
                            isRefreshingRef.current = false
                        })
                }

                return Promise.reject(error)
            }
        )

        // Cleanup function to remove interceptors
        return () => {
            // AXIOS_INSTANCE.interceptors.request.eject(requestInterceptor)
            AXIOS_INSTANCE.interceptors.response.eject(responseInterceptor)
        }
    }, [mutateRefreshToken, token, tokenSetter])
}
